/**
 * MFA (TOTP) step-up helpers.
 *
 * Supabase issues an `aal1` session after password / OAuth sign-in even when the user has a
 * verified factor; the server (gateway, services, RLS) now rejects aal1 for those users.
 * The SPA must therefore never treat an aal1 session of an MFA user as signed in, and must
 * route to the challenge when the API answers 403 `MFA_STEP_UP_REQUIRED`.
 *
 * Mirrors supabase-js `mfa.getAuthenticatorAssuranceLevel()` (local branch): currentLevel is
 * the JWT `aal` claim; nextLevel is `aal2` when `session.user.factors` has a verified factor.
 */
import type { Session } from "@supabase/supabase-js";

/** Stable server error code (shared-utils/mfaAssurance.js). Distinct from family `MFA_REQUIRED` (enrol). */
export const MFA_STEP_UP_REQUIRED_CODE = "MFA_STEP_UP_REQUIRED";
export const MFA_STEP_UP_REQUIRED_EVENT = "tellerwand:mfa-step-up-required";

export interface SessionMfaStepUp {
  required: boolean;
  factorId: string | null;
}

function decodeBase64Url(segment: string): string {
  const base64 = segment.replaceAll("-", "+").replaceAll("_", "/");
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  return globalThis.atob(padded);
}

/**
 * Read the `aal` claim from an access token (no verification — UI routing only).
 */
export function readAccessTokenAal(accessToken: string | null | undefined): string | null {
  if (typeof accessToken !== "string") {
    return null;
  }
  const parts = accessToken.split(".");
  if (parts.length !== 3) {
    return null;
  }
  try {
    const payload: unknown = JSON.parse(decodeBase64Url(parts[1]));
    if (payload && typeof payload === "object" && "aal" in payload) {
      const { aal } = payload as { aal?: unknown };
      return typeof aal === "string" ? aal : null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Whether this session belongs to an MFA-enrolled user who has not completed the challenge.
 * Prefers a verified TOTP factor as the challenge target.
 */
export function getSessionMfaStepUp(session: Session | null | undefined): SessionMfaStepUp {
  const factors = session?.user?.factors ?? [];
  const verified = factors.filter((factor) => factor.status === "verified");
  if (verified.length === 0) {
    return { required: false, factorId: null };
  }
  if (readAccessTokenAal(session?.access_token) === "aal2") {
    return { required: false, factorId: null };
  }
  const target = verified.find((factor) => factor.factor_type === "totp") ?? verified[0];
  return { required: true, factorId: target.id };
}

/**
 * True when the response is the server's MFA step-up rejection (body is read from a clone).
 */
export async function isMfaStepUpRequiredResponse(response: Response): Promise<boolean> {
  if (response.status !== 403) {
    return false;
  }
  try {
    const body: unknown = await response.clone().json();
    return (
      !!body &&
      typeof body === "object" &&
      (body as { code?: unknown }).code === MFA_STEP_UP_REQUIRED_CODE
    );
  } catch {
    return false;
  }
}

export function notifyMfaStepUpRequired(): void {
  globalThis.dispatchEvent?.(new Event(MFA_STEP_UP_REQUIRED_EVENT));
}

/**
 * Subscribe to server MFA step-up rejections; returns an unsubscribe function.
 */
export function subscribeToMfaStepUpRequired(listener: () => void): () => void {
  globalThis.addEventListener?.(MFA_STEP_UP_REQUIRED_EVENT, listener);
  return () => globalThis.removeEventListener?.(MFA_STEP_UP_REQUIRED_EVENT, listener);
}

/**
 * Wrap a fetch implementation so 403 MFA_STEP_UP_REQUIRED responses notify AuthContext.
 * The response itself is returned unchanged to the caller.
 */
export function withMfaStepUpDetection(fetchImpl: typeof fetch): typeof fetch {
  return async (input, init) => {
    const response = await fetchImpl(input, init);
    if (response.status === 403 && (await isMfaStepUpRequiredResponse(response))) {
      notifyMfaStepUpRequired();
    }
    return response;
  };
}
